import { UserCheck, UserPlus, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { VideoFilterBar, useVideoFilters } from '@/components/video-filters';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Channel, Video } from '@meradio/shared-types';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { 
  getChannelMetadata, 
  triggerManualImport, 
  followChannel, 
  unfollowChannel,
  selectSelectedChannel,
  selectIsChannelsLoading,
  selectChannelsError,
  selectIsChannelFollowed
} from '../channelsSlice';
import {
  getChannelVideos,
  selectChannelVideos,
  selectIsLoadingChannelVideos,
  selectIsLoadingMoreChannelVideos,
  selectChannelVideosHasMore,
  selectChannelVideosError,
  selectAllAudioFeatures
} from '../../videos/videosSlice';
import { setCurrentVideo, addToQueue, setQueue, playVideo } from '@/features/player/playerSlice';
import VideoTable from '@/components/table/VideoTable';
import { analyzeVideo } from '@/services/audio.api';
import { Button, buttonClasses, YouTubeChannelLink, Tooltip } from '@/components/ui';
import AddToCratePopover from '@/features/crates/AddToCratePopover';

const ChannelDetail: React.FC = () => {
  const [crateTarget, setCrateTarget] = useState<Video | null>(null);
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const selectedChannel = useAppSelector(selectSelectedChannel);
  const isLoading = useAppSelector(selectIsChannelsLoading);
  const error = useAppSelector(selectChannelsError);
  const isFollowed = useAppSelector(selectIsChannelFollowed(channelId || ''));
  
  const channelVideos = useAppSelector(selectChannelVideos(channelId || ''));
  const isLoadingVideos = useAppSelector(selectIsLoadingChannelVideos);
  const isLoadingMore = useAppSelector(selectIsLoadingMoreChannelVideos);
  const hasMoreVideos = useAppSelector(selectChannelVideosHasMore);
  const videosError = useAppSelector(selectChannelVideosError);
  const audioFeatures = useAppSelector(selectAllAudioFeatures);
  const filterState = useVideoFilters(channelVideos, audioFeatures);

  const { isFollowing, isUnfollowing, isImporting } = useAppSelector(state => state.channels);

  const handlePlayVideo = (video: Video) => {
    dispatch(setQueue(channelVideos));
    dispatch(playVideo(video));
  };

  const handleAddToQueue = (video: Video) => {
    dispatch(addToQueue(video));
  };

  const handleAnalyze = async (video: Video) => {
    try {
      await analyzeVideo(video.videoId);
    } catch {
      console.error('Failed to queue analysis');
    }
  };

  useEffect(() => {
    if (channelId) {
      dispatch(getChannelMetadata(channelId));
      dispatch(getChannelVideos({ channelId }));
    }
  }, [channelId, dispatch]);

  const handleFollowToggle = async () => {
    if (!selectedChannel) return;

    try {
      if (isFollowed) {
        await dispatch(unfollowChannel(selectedChannel.channelId)).unwrap();
      } else {
        await dispatch(followChannel(selectedChannel)).unwrap();
      }
    } catch (error) {
      console.error('Failed to update follow status', error);
    }
  };

  const handleManualImport = async () => {
    if (!channelId) return;

    try {
      await dispatch(triggerManualImport(channelId)).unwrap();

      setTimeout(() => {
        dispatch(getChannelVideos({ channelId }));
      }, 2000);
    } catch (error) {
      console.error('Failed to trigger manual import', error);
    }
  };

  const handleBack = () => {
    navigate('/channels');
  };

  if (!channelId) {
    return (
      <div className="p-6 text-center">
        <h1 className="text-2xl font-bold text-meradio-dark mb-4">Invalid Channel</h1>
        <Link to="/channels" className={buttonClasses('primary')}>
          Back to Channels
        </Link>
      </div>
    );
  }

  if (error && !selectedChannel) {
    return (
      <div className="p-6 text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-meradio-dark mb-4">Channel Not Found</h1>
        <p className="text-meradio-dark-light mb-6">{error}</p>
        <Button variant="primary" onClick={handleBack}>
          Back to Channels
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-primary">
      <div className="max-w-7xl mx-auto px-0 md:px-6 lg:px-8 py-4 md:py-8">
        {isLoading && !selectedChannel && (
          <div className="px-4 md:px-0 animate-pulse flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-dark-secondary rounded-full" />
              <div className="h-6 bg-dark-secondary rounded w-48" />
            </div>
            <div className="flex space-x-3">
              <div className="w-28 h-10 bg-dark-secondary rounded" />
              <div className="w-36 h-10 bg-dark-secondary rounded" />
            </div>
          </div>
        )}

        {selectedChannel && (
          <div className="px-4 md:px-0 flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3 min-w-0">
              {selectedChannel.thumbnailUrl ? (
                <img
                  src={selectedChannel.thumbnailUrl}
                  alt={`${selectedChannel.name} thumbnail`}
                  className="w-10 h-10 rounded-full flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-full flex-shrink-0 bg-gradient-to-br from-meradio-primary to-meradio-secondary flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {selectedChannel.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <h1 className="text-xl font-bold text-meradio-dark truncate">
                {selectedChannel.name}
              </h1>
              <YouTubeChannelLink
                channelId={selectedChannel.channelId}
                name={selectedChannel.name}
                className="text-meradio-dark-light dark:text-text-dark-secondary"
              />
            </div>

            <div className="flex items-center space-x-3 flex-shrink-0">
              <Tooltip label={isFollowed ? 'Following' : 'Follow'}>
                <Button
                  onClick={handleFollowToggle}
                  disabled={isFollowing || isUnfollowing}
                  isLoading={isFollowing || isUnfollowing}
                  variant={isFollowed ? 'secondary' : 'primary'}
                  aria-label={isFollowed ? 'Unfollow channel' : 'Follow channel'}
                >
                  {isFollowed ? <UserCheck className="w-4 h-4 md:hidden" /> : <UserPlus className="w-4 h-4 md:hidden" />}
                  <span className="hidden md:inline">
                    {isFollowing || isUnfollowing
                      ? isFollowed ? 'Unfollowing...' : 'Following...'
                      : isFollowed ? '✓ Following' : '+ Follow'}
                  </span>
                </Button>
              </Tooltip>

              {isFollowed && (
                <Tooltip label="Manual import">
                  <Button
                    onClick={handleManualImport}
                    disabled={isImporting}
                    isLoading={isImporting}
                    variant="outline"
                    aria-label="Manual import"
                  >
                    <Zap className="w-4 h-4 md:hidden" />
                    <span className="hidden md:inline">{isImporting ? 'Importing...' : '⚡ Manual Import'}</span>
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        <div className="bg-transparent md:bg-dark-tertiary rounded-none md:rounded-xl shadow-none md:shadow-sm p-0 md:p-8">
          <VideoFilterBar {...filterState} channels={[]} totalCount={channelVideos.length} />
          {isLoadingVideos && (
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }, (_, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-dark-secondary rounded-lg">
                  <div className="w-12 h-12 bg-neutral-300 rounded" />
                  <div className="flex-1">
                    <div className="h-4 bg-neutral-300 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-neutral-300 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {videosError && !isLoadingVideos && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold text-meradio-dark mb-2">
                Failed to Load Videos
              </h3>
              <p className="text-meradio-dark-light mb-4">{videosError || 'An error occurred while loading videos.'}</p>
              <Button
                variant="primary"
                onClick={() => channelId && dispatch(getChannelVideos({ channelId }))}
              >
                Try Again
              </Button>
            </div>
          )}

          {!isLoadingVideos && !videosError && channelVideos.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎵</div>
              <h3 className="text-lg font-semibold text-meradio-dark mb-2">
                No Videos Found
              </h3>
              <p className="text-meradio-dark-light mb-4">
                This channel doesn't have any imported videos yet.
              </p>
              {isFollowed && (
                <Button variant="primary" onClick={handleManualImport}>
                  Import Videos
                </Button>
              )}
            </div>
          )}

          {!isLoadingVideos && channelVideos.length > 0 && (
            <VideoTable
              videos={filterState.filteredVideos}
              audioFeatures={audioFeatures}
              onPlayVideo={handlePlayVideo}
              onAddToQueue={handleAddToQueue}
              onAnalyze={handleAnalyze}
              onAddToCrate={setCrateTarget}
            />
          )}

          <AddToCratePopover
            videoId={crateTarget?.videoId ?? null}
            videoTitle={crateTarget?.title}
            open={crateTarget !== null}
            onClose={() => setCrateTarget(null)}
          />

          {!isLoadingVideos && channelVideos.length > 0 && hasMoreVideos && (
            <div className="text-center mt-8">
              <button
                onClick={() => channelId && dispatch(getChannelVideos({
                  channelId,
                  offset: channelVideos.length,
                  reset: false,
                }))}
                disabled={isLoadingMore}
                className="
                  px-6 py-3 bg-neutral-100 hover:bg-neutral-200
                  dark:bg-bg-dark-tertiary dark:hover:bg-border-dark
                  text-neutral-700 dark:text-text-dark-secondary
                  rounded-xl transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelDetail;
